import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { SkeletonBox } from './Skeleton';
import {
  fetchDeletedItems,
  restoreRefund,
  restoreDispute,
  restoreInventory,
  restorePayment,
  saveDeleteNote,
  DeletedItem,
} from '../services/api';
import { fluidSheet, fluidScrim, fluidCascade, useLandWindow } from './Fluid'; // [FLUID-2 2026-10-12]
// [LIQUID-GLASS 2026-11-02, user] the panel, cards, tags and buttons use the shared liquid glass recipe
import {
  lgSurface, lgSurfaceShadow, lgCard, lgControl, lgControlBg, lgControlHover, lgPress, lgSunken, lgSquircle, lgHair,
  lgButtonPrimary, lgButtonSecondary, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';
import { alertDialog } from './dialogService';
import { useAuth } from '../contexts/AuthContext';


/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// DateTimeFormat, not NumberFormat: this one is `Date.prototype.toLocaleString`, which formats a
// DATE despite sharing its name with the number method. (tsc caught the mix-up.)
// [DATE-STYLE 2026-09-29, user] "Sep 29, 2026, 4:28 PM" (the year was missing: "Sep 29, 4:28 PM").
const MONTH_SHORT_DAY_TIME_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const MONTH_SHORT_DAY_YEAR_UTC_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged?: () => void; // called after a restore so badge count refreshes
  unread?: number; // # of trashed rows not yet acknowledged (drives the badge)
  onMarkRead?: (keys: string[]) => void; // acknowledge the shown rows -> clears badge
}

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 999;
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
`;

const Backdrop = styled.div<{ $open: boolean }>`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.18);
  ${(p) => fluidScrim(p.$open) /* [FLUID-2 2026-10-12] fades in, out a little quicker */}
`;

/* [LIQUID-GLASS 2026-11-02, user] The same floating frosted sheet as App Alerts (styles/liquidGlass.ts): 10 px clear of
   the window's top, right and bottom edges, smooth round corners. Was a flat strip on the right edge with blur(48px)
   saturate(180%), whose colour boost turned the page behind into a muddy tint. Hidden 24 px past its own width. */
const Panel = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  bottom: 10px;
  width: 452px;
  max-width: calc(95vw / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: ${(p) => p.theme.text};
  ${lgSurface(34)}
  ${lgSquircle(26)}
  /* Shadow only when open — otherwise the off-screen panel's drop shadow
     bleeds back into the right edge of the page. */
  box-shadow: ${(p) => (p.$open ? lgSurfaceShadow(p) : 'none')};

  /* [PHONE-LAYOUT 2026-10-22] Phones: one full-width sheet, like App Alerts and the bell (95vw left a thin strip of
     page beside it and each drawer had its own width). The header's close button closes it.
     [LIQUID-GLASS 2026-11-02] It floats 8 px clear of the screen's edges, with 22 px corners. */
  @media (max-width: 768px) {
    top: 8px;
    right: 8px;
    bottom: 8px;
    width: calc(100% - 16px);
    max-width: none;
    border-radius: 22px;
  }

  /* [FLUID-2 2026-10-12, user] Slides in on the spring (0.34 s), out quicker (0.22 s) — components/Fluid.tsx. */
  ${(p) => fluidSheet(p.$open, 'translateX(calc(100% + 24px))')}
`;

/* [LIQUID-GLASS 2026-11-02, user] No grey band any more: the title sits on the glass, over a hairline. */
const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 14px 12px 20px;
  flex-shrink: 0;
  border-bottom: 1px solid ${lgHair};

  /* [PHONE-LAYOUT 2026-10-22] Phones: the same header height with the bigger buttons. */
  @media (max-width: 768px) {
    padding: 12px 12px 12px 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${(p) => p.theme.text};
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
`;

/* [LIQUID-GLASS 2026-11-02] A small glass capsule. */
const Badge = styled.span`
  background: ${lgControlBg};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28), 0 0 0 1px ${lgHair};
  color: ${(p) => p.theme.text};
  font-size: 11px;
  font-weight: 700;
  min-width: 22px;
  height: 22px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 7px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// "Mark as Read" pill — sits left of the close button. Acknowledges the trashed
// rows so the header badge clears (it reads as "pending" otherwise). Same
// translucent-white treatment as the close button so it reads as part of the
// drawer chrome rather than a primary action competing with Revert.
// [BUTTON-STYLE 2026-09-30] Now the secondary button style.
// [LIQUID-GLASS 2026-11-02] The glass capsule (squish + press light).
const MarkReadBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  /* [PHONE-LAYOUT 2026-10-22] Phones: 36px tall, level with the close button. */
  @media (max-width: 768px) {
    height: 36px;
  }
`;

/* [LIQUID-GLASS 2026-11-02] A round glass button that squishes and glows where it is pressed. */
const CloseBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgControl}
  ${lgPress}
  position: relative;
  border: none;
  color: ${(p) => p.theme.text};
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 36px tap target. */
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 15px;
  }
`;

const Body = styled.div<{ $land: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  /* [LIQUID-GLASS 2026-11-02] Cards fade softly as they scroll up under the header. */
  -webkit-mask-image: linear-gradient(180deg, transparent 0, #000 12px);
  mask-image: linear-gradient(180deg, transparent 0, #000 12px);
  /* [FLUID-2 2026-10-12, user] Right after opening (and when the list lands) the cards glide in from the right,
     one after another, as the panel settles. Only in that window (useLandWindow), so a card that changes later
     does not replay it. */
  ${(p) => (p.$land ? fluidCascade('> *', 0, 20, 8, 'softSlide') /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

const Empty = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${(p) => p.theme.muted};
  font-size: 13px;
`;

/* [LIQUID-GLASS 2026-11-02, user] A glass card on the glass sheet (light top edge, smooth corners); a hover brightens
   it a little instead of lifting it. */
const ItemCard = styled.div`
  position: relative;
  ${lgCard}
  ${lgSquircle(20)}
  padding: 13px 15px;
  margin-bottom: 10px;
  transition: background-color 150ms ease;

  &:hover {
    background-color: ${lgControlHover};
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

/* [LIQUID-GLASS 2026-11-02, user] A small glossy capsule in the kind's colour, with a soft glow of that colour. */
const tagColour = (kind: string, dark: boolean) => (kind === 'dispute' ? '#dc2626' : kind === 'inventory' ? '#0ea5e9'
  : kind === 'payment' ? (dark ? '#2f9e6b' : '#12855f') : (dark ? '#f5a524' : '#f59e0b'));
const SourceTag = styled.span<{ $kind: 'refund' | 'dispute' | 'inventory' | 'payment' }>`
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 3px 10px;
  border-radius: 999px;
  color: #fff;
  background-color: ${(p) => tagColour(p.$kind, p.theme.colorScheme === 'dark')};
  background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.38), rgba(255, 255, 255, 0) 58%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45), 0 4px 12px -4px ${(p) => tagColour(p.$kind, p.theme.colorScheme === 'dark')};

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11px, the phone label floor. */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

/* [LIQUID-GLASS 2026-11-02, user] A softer red on dark (#dc2626 there was 3 : 1, hard to read). */
const Amount = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')};
`;

const Line = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.text};
  margin: 2px 0;

  span {
    color: ${(p) => p.theme.muted};
    margin-right: 6px;
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a long charge id, email or website breaks onto the next line inside the
     card instead of pushing past its edge. */
  @media (max-width: 768px) {
    overflow-wrap: anywhere;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 10px;
  gap: 8px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the "Deleted ..." time on its own line, then Note and Revert as two equal
     36px buttons (they used to squeeze, and Revert's label was cut). */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    & > button {
      flex: 1 1 0;
      min-width: 0;
      height: 36px;
    }
  }
`;

const DeletedAt = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme.muted};
  margin-right: auto;

  /* [PHONE-LAYOUT 2026-10-22] See Actions. */
  @media (max-width: 768px) {
    flex: 1 0 100%;
    margin-right: 0;
  }
`;

// "Add a Note" / "Note" button (the note's text shows as the hover tooltip via the title
// attribute). [BUTTON-STYLE 2026-09-30] The secondary style either way; the label and icon
// still tell "Note" from "Add a Note".
// [BUTTON-STYLE 2026-09-30] Plain button, but a note that EXISTS still shows as a soft green tint
// (the $has state the old style drew), so the Bin still tells you which rows carry a note.
// [LIQUID-GLASS 2026-11-02] The glass capsule; a row WITH a note keeps its soft green tint, now with a green rim.
const NoteBtn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $has: boolean }>`
  ${lgButtonSecondary}
  ${(p) => (p.$has
    ? `background-color: ${p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.14)' : 'rgba(16,185,129,0.12)'};
       &::before { background: linear-gradient(180deg, rgba(134,243,192,0.85), rgba(74,205,139,0.18) 40%, rgba(74,205,139,0.40)); }`
    : '')}
`;

// Inline note editor that drops in below the actions when "Add a Note" is clicked.
const NoteEditor = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* [FLUID-8 2026-10-12] the note box and its buttons ripple in (soft) when Add a Note opens it */
  ${fluidCascade('> *', 0, 20, 2)}
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 62px;
  resize: vertical;
  border-radius: 12px;
  padding: 8px 10px;
  font-size: 12.5px;
  font-family: inherit;
  line-height: 1.45;
  color: ${(p) => p.theme.text};
  /* [LIQUID-GLASS 2026-11-02] Pressed into the glass; a soft green ring and glow while you type. */
  ${lgSunken}
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &::placeholder { color: ${(p) => p.theme.dim}; } /* [FULL-INK 2026-09-01] stays grey on purpose */
`;

const NoteEditorRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 36px tall tap targets. */
  @media (max-width: 768px) {
    & > button { height: 36px; }
  }
`;

// [BUTTON-STYLE 2026-09-30] Add = the green primary, Cancel = the secondary.
// [LIQUID-GLASS 2026-11-02] Glass capsules (squish + press light).
const NoteSaveBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
`;

const NoteCancelBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
`;

// [BUTTON-STYLE 2026-09-30] Revert = the green primary. [LIQUID-GLASS 2026-11-02] the glossy green capsule.
const RestoreBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
`;

function fmtCurrency(n: number): string {
  return `$${MONEY_FMT.format(n)}`;
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return MONTH_SHORT_DAY_YEAR_UTC_FMT.format(date);
}

function fmtDeletedAt(d: string | null): string {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  return `Deleted ${MONTH_SHORT_DAY_TIME_FMT.format(date)}`;
}

const DeletedDrawer: React.FC<Props> = ({ open, onClose, onChanged, unread = 0, onMarkRead }) => {
  // Restoring (un-deleting) a refund/dispute is a change, so it's admin-only —
  // non-admins can view the trash but the Revert button is hidden, and the
  // restore endpoints are gated by requireAdmin on the backend.
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [items, setItems] = useState<DeletedItem[]>([]);
  const [loading, setLoading] = useState(false);
  // [PANEL-KEEP 2026-10-13, user] "make the fix" — this panel used to throw its list away on every opening and show skeleton
  // cards until the server answered again. Once a list has loaded, a reopening shows it AT ONCE and refreshes it behind
  // (the skeleton only before the very first list), like App Alerts. A card acted on in between is re-checked by the server.
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // [DELETE-NOTE 2026-06-29] Which card's note editor is open + its draft text + save busy.
  const [noteEditKey, setNoteEditKey] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchDeletedItems();
      setItems(list);
      setLoaded(true);
    } catch (err) {
      console.error('Failed to load deleted items:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // [FLUID-2 2026-10-12] The window in which the cards glide in: the panel opening, then the list landing.
  // [PANEL-KEEP] a refresh behind the shown list never replays the glide (same key once a list has loaded)
  const land = useLandWindow(open, loaded ? 'list' : loading ? 'loading' : 'ready');

  // [DELETE-NOTE 2026-06-29] Open the inline note editor for a card, seeded with its
  // existing note (so reopening shows the old text, editable). Toggling closes it.
  const toggleNoteEditor = (item: DeletedItem, key: string) => {
    if (noteEditKey === key) { setNoteEditKey(null); return; }
    setNoteDraft(item.note || '');
    setNoteEditKey(key);
  };

  const submitNote = async (item: DeletedItem) => {
    setSavingNote(true);
    try {
      const saved = await saveDeleteNote(item.source, item.id, noteDraft);
      setItems((prev) => prev.map((x) =>
        x.source === item.source && x.id === item.id ? { ...x, note: saved } : x
      ));
      setNoteEditKey(null);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to save the note.';
      alertDialog({ title: 'Note not saved', message: msg, destructive: true });
    } finally {
      setSavingNote(false);
    }
  };

  const handleRestore = async (item: DeletedItem) => {
    const key = `${item.source}-${item.id}`;
    setBusyId(key);
    try {
      if (item.source === 'refund') await restoreRefund(item.id);
      else if (item.source === 'dispute') await restoreDispute(item.id);
      else if (item.source === 'payment') await restorePayment(item.id);
      else await restoreInventory(item.id);
      setItems((prev) => prev.filter((x) => !(x.source === item.source && x.id === item.id)));
      onChanged?.();
    } catch (err: any) {
      console.error('Failed to restore:', err);
      const msg = err?.response?.data?.error || 'Failed to restore row.';
      alertDialog({ title: 'Restore failed', message: msg, destructive: true });
    } finally {
      setBusyId(null);
    }
  };

  // Portal to <body> so the fixed drawer always anchors to the viewport,
  // regardless of transformed/filtered ancestors.
  // [AUTO-REFRESH 2026-08-03] data-reload-block only while open — this drawer stays mounted.
  return createPortal(
    <Overlay $open={open} data-reload-block={open ? '' : undefined}>
      <Backdrop $open={open} onClick={onClose} />
      <Panel $open={open}>
        <PanelHeader>
          <HeaderLeft>
            <i className="fas fa-trash-alt"></i>
            <PanelTitle>Trash</PanelTitle>
            <Badge>{items.length}</Badge>
          </HeaderLeft>
          <HeaderRight>
            {/* Only offer "Mark as Read" when there's actually an unacknowledged
                count to clear — otherwise the badge is already gone. */}
            {unread > 0 && onMarkRead && (
              <MarkReadBtn
                onClick={() => onMarkRead(items.map((it) => `${it.source}-${it.id}`))}
                title="Clear the trash badge — mark these as seen"
              >
                <i className="fas fa-check"></i>
                Mark as Read
              </MarkReadBtn>
            )}
            <CloseBtn onClick={onClose} title="Close">
              <i className="fas fa-times"></i>
            </CloseBtn>
          </HeaderRight>
        </PanelHeader>

        <Body $land={land}>
          {loading && !loaded ? ( /* [PANEL-KEEP] skeleton only before the first list */
            // Skeleton mirrors the real ItemCard list (tag + amount row, then
            // detail lines) instead of plain "Loading…" text.
            <>
              {Array.from({ length: 4 }).map((_, i) => (
                <ItemCard key={i} aria-busy="true">
                  <TopRow>
                    <SkeletonBox $w="64px" $h="16px" $radius="5px" />
                    <SkeletonBox $w="70px" $h="14px" />
                  </TopRow>
                  <SkeletonBox $w="85%" $h="10px" $mt="10px" />
                  <SkeletonBox $w="72%" $h="10px" $mt="8px" />
                  <SkeletonBox $w="60%" $h="10px" $mt="8px" />
                </ItemCard>
              ))}
            </>
          ) : items.length === 0 ? (
            <Empty>No deleted items.</Empty>
          ) : (
            items.map((item) => {
              const key = `${item.source}-${item.id}`;
              const isInv = item.source === 'inventory';
              return (
                <ItemCard key={key}>
                  <TopRow>
                    <SourceTag $kind={item.source}>{item.source}</SourceTag>
                    {/* [PAYMENT-DELETE 2026-07-17] Payments are money-IN → show positive like
                        inventory; only refunds/disputes render as a negative (money-out). */}
                    <Amount>{(isInv || item.source === 'payment') ? fmtCurrency(Number(item.amount) || 0) : `-${fmtCurrency(Number(item.amount) || 0)}`}</Amount>
                  </TopRow>
                  <Line><span>Website:</span>{item.website || '—'}</Line>
                  <Line><span>Email:</span>{item.email || '—'}</Line>
                  {isInv ? (
                    <>
                      <Line><span>PM:</span>{item.pm || '—'}</Line>
                      <Line><span>Month:</span>{item.month_str || '—'}</Line>
                      {item.account_name && <Line><span>Brand:</span>{item.account_name}</Line>}
                    </>
                  ) : (
                    <>
                      <Line><span>Account:</span>{item.account_name || '—'}</Line>
                      <Line><span>Date:</span>{fmtDate(item.event_date)}{item.month_str ? ` · ${item.month_str}` : ''}</Line>
                      {item.charge_id && <Line><span>Charge:</span>{item.charge_id}</Line>}
                      {item.source === 'dispute' && item.dispute_status && (
                        <Line><span>Status:</span>{item.dispute_status}</Line>
                      )}
                    </>
                  )}
                  <Actions>
                    <DeletedAt>{fmtDeletedAt(item.deleted_at)}</DeletedAt>
                    <NoteBtn
                      $has={!!item.note}
                      onClick={() => toggleNoteEditor(item, key)}
                      title={item.note || 'Add a note explaining why this was deleted'}
                    >
                      <i className={`fas ${item.note ? 'fa-comment-dots' : 'fa-comment-medical'}`}></i>
                      {item.note ? 'Note' : 'Add a Note'}
                    </NoteBtn>
                    {isAdmin && (
                      <RestoreBtn
                        onClick={() => handleRestore(item)}
                        disabled={busyId === key}
                        title="Restore this row"
                      >
                        <i className="fas fa-undo"></i>
                        {busyId === key ? 'Restoring…' : 'Revert'}
                      </RestoreBtn>
                    )}
                  </Actions>

                  {noteEditKey === key && (
                    <NoteEditor>
                      <NoteTextarea
                        autoFocus
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') setNoteEditKey(null); }}
                        placeholder="Why was this deleted? (reason)"
                        maxLength={1000}
                      />
                      <NoteEditorRow>
                        <NoteCancelBtn type="button" onClick={() => setNoteEditKey(null)}>
                          Cancel
                        </NoteCancelBtn>
                        <NoteSaveBtn type="button" onClick={() => submitNote(item)} disabled={savingNote}>
                          {savingNote ? 'Adding…' : 'Add'}
                        </NoteSaveBtn>
                      </NoteEditorRow>
                    </NoteEditor>
                  )}
                </ItemCard>
              );
            })
          )}
        </Body>
      </Panel>
    </Overlay>,
    document.body
  );
};

export default DeletedDrawer;
